import { useState, useCallback, useRef, useEffect } from "react";
import { Effect } from "effect";
import { getLinks } from "../services/linkService.js";
import type { Link } from "@link-storage/contracts";

const DEFAULT_LIMIT = 20;

export function useInfiniteLinks(tag?: string | null) {
  const [links, setLinks] = useState<Link[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const isFetchingRef = useRef(false);

  const fetchPage = useCallback(
    async (cursor: string | null) => {
      if (isFetchingRef.current) return;
      isFetchingRef.current = true;
      setIsLoading(true);

      const result = await Effect.runPromise(
        Effect.either(getLinks(cursor, DEFAULT_LIMIT, tag))
      );

      isFetchingRef.current = false;
      setIsLoading(false);

      if (result._tag === "Left") {
        // fetch failed — stop trying
        setHasMore(false);
        return;
      }

      const { links: newLinks, nextCursor: newCursor } = result.right;

      setLinks((prev) => {
        const existingIds = new Set(prev.map((l) => l.id));
        const deduplicated = (newLinks as Link[]).filter(
          (l) => !existingIds.has(l.id)
        );
        return [...prev, ...deduplicated];
      });
      setNextCursor(newCursor);
      setHasMore(newCursor !== null);
    },
    [tag]
  );

  // Initial load
  useEffect(() => {
    setLinks([]);
    setNextCursor(null);
    setHasMore(true);
    fetchPage(null);
  }, [tag, fetchPage]);

  // IntersectionObserver — trigger next page when sentinel is visible
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !isFetchingRef.current) {
          fetchPage(nextCursor);
        }
      },
      { threshold: 0.1 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, nextCursor, fetchPage]);

  const reset = useCallback(() => {
    setLinks([]);
    setNextCursor(null);
    setHasMore(true);
    fetchPage(null);
  }, [fetchPage]);

  return { links, isLoading, hasMore, sentinelRef, reset };
}
